import { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Search, Trash2 } from 'lucide-react';
import { getAuth, deleteUser } from 'firebase/auth';
import { httpsCallable, getFunctions } from 'firebase/functions';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [userToDelete, setUserToDelete] = useState(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const functions = getFunctions();

  // Filter users based on search query
  const filteredUsers = users.filter(user => {
    const searchLower = searchQuery.toLowerCase();
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
    const email = (user.email || '').toLowerCase();
    const role = user.isEditor ? 'editor' : 'user';

    return fullName.includes(searchLower) ||
           email.includes(searchLower) ||
           role.includes(searchLower);
  });

  useEffect(() => {
    // Check if current user is an editor
    if (!currentUser?.profile?.isEditor) {
      navigate('/');
      return;
    }

    const fetchUsers = async () => {
      try {
        const usersCollection = collection(db, 'users');
        const snapshot = await getDocs(usersCollection);
        const usersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setUsers(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [currentUser, navigate]);

  const toggleEditorRole = async (userId, currentValue) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        isEditor: !currentValue,
        updatedAt: new Date().toISOString()
      });

      // Update local state
      setUsers(users.map(user => {
        if (user.id === userId) {
          return { ...user, isEditor: !currentValue };
        }
        return user;
      }));
    } catch (error) {
      console.error('Error updating user role:', error);
    }
  };

  const toggleViewSecret = async (userId, currentValue) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        viewSecret: !currentValue,
        updatedAt: new Date().toISOString()
      });

      // Update local state
      setUsers(users.map(user => {
        if (user.id === userId) {
          return { ...user, viewSecret: !currentValue };
        }
        return user;
      }));
    } catch (error) {
      console.error('Error updating view secret permission:', error);
    }
  };

  const handleDeleteUser = async (user) => {
    setUserToDelete(user);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      // Call the Firebase Function to delete the user from Auth
      const deleteUserAuth = httpsCallable(functions, 'deleteUserAccount');
      await deleteUserAuth({ uid: userToDelete.id });

      // Delete user document from Firestore
      await deleteDoc(doc(db, 'users', userToDelete.id));

      // Update local state
      setUsers(users.filter(u => u.id !== userToDelete.id));
      setUserToDelete(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-page dark:bg-page-dark">
        <p className="text-text-secondary dark:text-text-secondary-dark">Loading users...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page dark:bg-page-dark flex flex-col">
      <div className="h-screen max-w-4xl mx-auto w-full flex flex-col">
        <div className="flex-none bg-surface dark:bg-surface-dark shadow-surface dark:shadow-surface-dark">
          <div className="flex items-center p-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center text-text-secondary dark:text-text-secondary-dark hover:text-text-primary dark:hover:text-text-primary-dark"
            >
              <ChevronLeft className="h-6 w-6 mr-1" />
              Back
            </button>
            <div className="flex-1 flex justify-center">
              <h2 className="text-xl font-semibold text-text-primary dark:text-text-primary-dark">
                Users
              </h2>
            </div>
            <div className="w-[72px]" /> {/* Spacer to center the title */}
          </div>
          
          {/* Search Bar */}
          <div className="px-4 pb-4">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search users by name, email, or role..."
                className="w-full p-3 pl-10 rounded-lg bg-input-bg dark:bg-input-bg-dark 
                         text-text-primary dark:text-text-primary-dark 
                         border border-input-border dark:border-input-border-dark 
                         focus:ring-2 focus:ring-button dark:focus:ring-button-dark focus:border-transparent"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 
                              text-text-tertiary dark:text-text-tertiary-dark" />
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto p-4">
            <div className="bg-surface dark:bg-surface-dark rounded-lg shadow-surface dark:shadow-surface-dark">
              <div className="divide-y divide-default dark:divide-default-dark">
                {filteredUsers.map(user => (
                  <div key={user.id} className="px-4 py-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h3 className="text-sm font-medium text-text-primary dark:text-text-primary-dark">
                          {user.firstName} {user.lastName}
                        </h3>
                        <p className="text-sm text-text-secondary dark:text-text-secondary-dark">{user.email}</p>
                      </div>
                      <button
                        onClick={() => handleDeleteUser(user)}
                        className="p-2 rounded-full hover:bg-status-error-bg dark:hover:bg-status-error-bg-dark
                                 text-text-tertiary dark:text-text-tertiary-dark
                                 hover:text-status-error-text dark:hover:text-status-error-text-dark
                                 transition-colors"
                        title="Delete user"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="flex items-center gap-6">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={user.isEditor || false}
                          onChange={() => toggleEditorRole(user.id, user.isEditor)}
                          className="sr-only peer"
                        />
                        <div className="relative w-11 h-6 bg-surface dark:bg-surface-dark rounded-full peer peer-focus:ring-2 peer-focus:ring-button dark:peer-focus:ring-button-dark peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-surface dark:after:bg-surface-dark after:border-default dark:after:border-default-dark after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-button dark:peer-checked:bg-button-dark"></div>
                        <span className="ml-3 text-sm font-medium text-text-primary dark:text-text-primary-dark">Editor</span>
                      </label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={user.viewSecret || false}
                          onChange={() => toggleViewSecret(user.id, user.viewSecret)}
                          className="sr-only peer"
                        />
                        <div className="relative w-11 h-6 bg-surface dark:bg-surface-dark rounded-full peer peer-focus:ring-2 peer-focus:ring-button dark:peer-focus:ring-button-dark peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-surface dark:after:bg-surface-dark after:border-default dark:after:border-default-dark after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-button dark:peer-checked:bg-button-dark"></div>
                        <span className="ml-3 text-sm font-medium text-text-primary dark:text-text-primary-dark">View Secret</span>
                      </label>
                    </div>
                  </div>
                ))}
                <div className="h-[100px]"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {userToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface dark:bg-surface-dark rounded-lg p-6 max-w-md mx-4 w-full">
            <h3 className="text-lg font-semibold mb-2 text-text-primary dark:text-text-primary-dark">
              Delete User
            </h3>
            <p className="text-text-secondary dark:text-text-secondary-dark mb-4">
              Are you sure you want to delete {userToDelete.firstName} {userToDelete.lastName}? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setUserToDelete(null)}
                className="px-4 py-2 rounded-lg bg-surface-hover dark:bg-surface-hover-dark
                         text-text-primary dark:text-text-primary-dark"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 rounded-lg bg-status-error-bg dark:bg-status-error-bg-dark
                         text-status-error-text dark:text-status-error-text-dark
                         hover:bg-status-error-bg-hover dark:hover:bg-status-error-bg-hover-dark"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users; 